import type { PlaceResult } from '@pf/api-types';
import { format } from '@pf/money';
import { keyBetween, newUuidv7 } from '@pf/sync-rules';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import {
  BedDouble,
  Crosshair,
  Flag,
  Footprints,
  Landmark,
  NotebookText,
  MapPin as PinIcon,
  Plane,
  Search,
  ShoppingBag,
  Ticket,
  TrainFront,
  Trash2,
  Utensils,
  X,
} from 'lucide-react';
import { type DragEvent, useEffect, useMemo, useState } from 'react';
import { api } from '../api.ts';
import { regionFor, useMapCatalog, useMe, useTrip, useTripCards } from '../app/data.ts';
import { stampNow, tripNow } from '../clock.ts';
import { cx } from '../kit/cx.ts';
import { DataTag, DateFlag, ItineraryRow, Leg, MapLegend, MapPlaceCard } from '../kit/index.ts';
import t from '../kit/type.module.css';
import { FieldMap, type MapPin, type MapRoute } from '../map/FieldMap.tsx';
import { clock, DAY_NAMES, hoursWarning } from '../model/hours.ts';
import { sunTimes, wallTime } from '../model/sun.ts';
import {
  amountIn,
  type Day,
  flag,
  type Item,
  type Planned,
  type Reservation,
  type TripModel,
  time,
  today,
} from '../model/trip.ts';
import { duration, instant, zoneLabel } from '../model/zones.ts';
import type { Edit } from '../sync.ts';
import s from './Planner.module.css';

/** W-01 planner: day chips, the day's timeline with legs and bookings, lists, and the day map. */
export function Planner() {
  const { tripId } = useParams({ from: '/app/trips/$tripId' });
  const search = useSearch({ from: '/app/trips/$tripId' });
  const navigate = useNavigate();
  const { model, save, error } = useTrip(tripId);
  const me = useMe();
  const cards = useTripCards();
  const [selected, setSelected] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  if (error)
    return (
      <p className={cx(s.pad, t.bodyS)} role="alert">
        {error.message}
      </p>
    );
  if (!model) return <p className={cx(s.pad, t.bodyS)}>Loading…</p>;
  const localToday = (d: Day) => today(d.tz);
  const day =
    model.days.find((d) => d.id === search.day) ??
    model.days.find((d) => d.date === localToday(d)) ??
    model.days[0];
  const pick = (id: string) =>
    void navigate({ to: '/trips/$tripId', params: { tripId }, search: { day: id } });

  const run = async (edits: Edit[]) => {
    const refused = await save(edits);
    setNotice(
      refused > 0 ? "A change couldn't be saved: it may have been deleted on the other device." : null,
    );
  };

  const move = (item: Item, to: { dayId?: string; listId?: string }, before?: Planned) => {
    const siblings = to.dayId
      ? (model.days.find((d) => d.id === to.dayId)?.items ?? [])
      : (model.lists.find((l) => l.id === to.listId)?.items ?? []);
    const rest = siblings.filter((p) => p.item.id !== item.id);
    const i = before ? rest.findIndex((p) => p.item.id === before.item.id) : rest.length;
    const sortKey = keyBetween(rest[i - 1]?.item.sortKey ?? null, rest[i]?.item.sortKey ?? null);
    void run([
      {
        table: 'item',
        id: item.id,
        tripId,
        fields: { dayId: to.dayId ?? null, listId: to.listId ?? null, sortKey },
      },
    ]);
  };

  const targets = [
    ...model.days.map((d) => ({ value: `day:${d.id}`, label: `Day ${pad(d.number)} · ${flag(d.date)}` })),
    ...model.lists.map((l) => ({ value: `list:${l.id}`, label: `List · ${l.name}` })),
  ];
  const moveTo = (item: Item, target: string) => {
    const [kind, id] = target.split(':');
    if (id) move(item, kind === 'day' ? { dayId: id } : { listId: id });
  };

  const spent = day
    ? spentOn(
        model,
        day,
        me.data?.user.id ?? '',
        day.destination?.localCurrency ?? me.data?.user.homeCurrency ?? 'EUR',
      )
    : 0;
  return (
    <div className={s.page}>
      <header className={s.top}>
        <div>
          <div className={cx(t.displayS, s.kicker)}>
            {[`// ${day?.destination?.name ?? model.title}`, cards.cards.find((c) => c.id === tripId)?.code]
              .filter(Boolean)
              .join(' · ')}
          </div>
          <div className={s.dayline}>
            {day && <DateFlag text={flag(day.date)} today={day.date === localToday(day)} />}
            <h1 className={t.displayL}>{day ? `Day ${pad(day.number)} / ${pad(day.count)}` : model.title}</h1>
          </div>
        </div>
        <DayChips
          model={model}
          current={day?.id}
          isToday={(d) => d.date === localToday(d)}
          onPick={pick}
          onDrop={(id, dayId) => {
            const item = findItem(model, id);
            if (item) move(item, { dayId });
          }}
        />
        {day && <AddPlace day={day} tripId={tripId} run={run} />}
      </header>
      {notice && (
        <p className={cx(s.notice, t.bodyS)} role="status">
          {notice}
        </p>
      )}
      <div className={s.body}>
        <section className={s.timeline} aria-labelledby="pf-plan">
          <h2 id="pf-plan" className={t.srOnly}>
            Plan
          </h2>
          {day && (
            <>
              <div className={s.stats}>
                <h3 className={cx(t.label, s.flat)}>
                  {[day.title, sunLine(day)].filter(Boolean).join(' · ') ||
                    `${day.items.length} stop${day.items.length === 1 ? '' : 's'}`}
                </h3>
                {walked(model, day, me.data?.user.id) > 0 && (
                  <DataTag label="Walked" value={km(walked(model, day, me.data?.user.id))} />
                )}
                {spent > 0 && (
                  <DataTag
                    label="Spent"
                    value={format(
                      spent,
                      day.destination?.localCurrency ?? me.data?.user.homeCurrency ?? 'EUR',
                    )}
                  />
                )}
              </div>
              {/* Bookings from earlier days: arrivals and check-outs lead the day (IMPL-CONNECT B6). */}
              <BookingMarkers model={model} date={day.date} part="ends" />
              <DropList
                items={day.items}
                model={model}
                nowId={day.date === localToday(day) ? currentStop(day) : null}
                visits={model.visits.filter((v) => v.dateLocal === day.date && v.userId === me.data?.user.id)}
                tz={day.tz}
                date={day.date}
                selected={selected}
                onSelect={setSelected}
                onDrop={(id, before) => {
                  const item = findItem(model, id);
                  if (item) move(item, { dayId: day.id }, before);
                }}
                onDelete={(p) => void run(deleteEdits(tripId, p))}
                targets={targets}
                onMove={moveTo}
                showLegs
              />
              {day.items.length === 0 && (
                <p className={cx(s.pad, t.bodyS, s.muted)}>
                  Nothing planned yet. Search a place above, or drag one from a list.
                </p>
              )}
              <BookingMarkers model={model} date={day.date} part="continues" />
            </>
          )}
          {model.lists.map((list) => (
            <div key={list.id} className={s.list}>
              {/* biome-ignore lint/a11y/noStaticElementInteractions: a drop target; each row's Move menu is the keyboard path */}
              <div
                className={cx(s.listHead, t.label)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  const item = findItem(model, e.dataTransfer.getData('text/pf-item'));
                  if (item) move(item, { listId: list.id });
                }}
              >
                <h3 className={cx(t.label, s.flat)}>List · {list.name}</h3>
                <span className={s.muted}>{list.items.length} · drag onto a day</span>
              </div>
              <DropList
                items={list.items}
                model={model}
                selected={selected}
                onSelect={setSelected}
                onDrop={(id, before) => {
                  const item = findItem(model, id);
                  if (item) move(item, { listId: list.id }, before);
                }}
                onDelete={(p) => void run(deleteEdits(tripId, p))}
                targets={targets}
                onMove={moveTo}
              />
            </div>
          ))}
        </section>
        <aside className={s.mapCol}>
          {day && (
            <DayMap
              model={model}
              day={day}
              selected={selected}
              onSelect={setSelected}
              me={me.data?.user.id}
              partnerName={me.data?.partner?.displayName}
              nowId={day.date === localToday(day) ? currentStop(day) : null}
            />
          )}
        </aside>
      </div>
    </div>
  );
}

const pad = (n: number) => String(n).padStart(2, '0');

/** "Sunrise 06:21 · Sunset 16:57" at the day's places. */
function sunLine(day: Day): string | null {
  const pts = day.items.flatMap((p) =>
    p.item.place?.lat != null && p.item.place?.lng != null ? [[p.item.place.lat, p.item.place.lng]] : [],
  );
  if (!day.date || pts.length === 0) return null;
  const lat = pts.reduce((a, p) => a + (p[0] ?? 0), 0) / pts.length;
  const lng = pts.reduce((a, p) => a + (p[1] ?? 0), 0) / pts.length;
  const sun = sunTimes(day.date, lat, lng);
  return sun ? `Sunrise ${wallTime(sun.rise, day.tz)} · Sunset ${wallTime(sun.set, day.tz)}` : null;
}

const walked = (m: TripModel, day: Day, me?: string) =>
  m.segments.filter((x) => x.dateLocal === day.date && x.userId === me).reduce((a, x) => a + x.distanceM, 0);
const km = (m: number) => (m < 1000 ? `${Math.round(m / 10) * 10} m` : `${(m / 1000).toFixed(1)} km`);

/** The stop whose slot holds the trip clock's time now (as TripNow on the apps). */
function currentStop(day: Day): string | null {
  const hhmm = wallTime(tripNow(), day.tz);
  const mins = (s: string) => Number(s.slice(0, 2)) * 60 + Number(s.slice(3, 5));
  const now = mins(hhmm);
  const hit = [...day.items].reverse().find((p) => {
    const start = time(p.item.startLocal);
    return start != null && mins(start) <= now && now < mins(start) + (p.item.durationMin ?? 60);
  });
  return hit?.item.id ?? null;
}

/** Optional string props without the empty ones (exactOptionalPropertyTypes). */
function defined<K extends string>(props: Record<K, string | null | undefined>): Partial<Record<K, string>> {
  return Object.fromEntries(
    Object.entries(props).filter(([, v]) => typeof v === 'string' && v !== ''),
  ) as Partial<Record<K, string>>;
}

function findItem(m: TripModel, id: string): Item | null {
  for (const p of [...m.days.flatMap((d) => d.items), ...m.lists.flatMap((l) => l.items)]) {
    if (p.item.id === id) return p.item;
    for (const sub of p.subItems) if (sub.item.id === id) return sub.item;
  }
  return null;
}

function deleteEdits(tripId: string, p: Planned): Edit[] {
  const now = stampNow();
  return [p, ...p.subItems].map((x) => ({
    table: 'item',
    id: x.item.id,
    tripId,
    fields: { deletedAt: now },
  }));
}

function spentOn(m: TripModel, day: Day, me: string, currency: string) {
  return m.entries
    .filter((e) => e.dateLocal === day.date)
    .reduce((sum, e) => {
      const share = e.ownerUserId
        ? e.ownerUserId === me
          ? e.amountMinor
          : 0
        : e.splits.filter((x) => x.userId === me).reduce((a, x) => a + x.amountMinor, 0);
      return sum + (amountIn(e, currency, share) ?? 0);
    }, 0);
}

const JOURNEY = new Set(['flight', 'train', 'bus', 'ferry']);
const hm = (local: string | null) => time(local) ?? '';
const dur = (r: Reservation) => {
  const a = instant(r.startLocal, r.startTz);
  const b = instant(r.endLocal, r.endTz ?? r.startTz);
  return a !== null && b !== null && b > a ? duration(Math.round((b - a) / 60_000)) : null;
};

/** A booking's line on the day, as the apps show it: route with both local times, or the stay. */
function bookingLine(r: Reservation, role: 'starts' | 'ends' | 'continues'): string {
  const d = r.details;
  if (JOURNEY.has(r.kind)) {
    if (role === 'ends')
      return [
        `From ${d.from ?? 'departure'}`,
        `left ${hm(r.startLocal)} ${zoneLabel(r.startTz, null)} the day before`,
        dur(r),
      ]
        .filter(Boolean)
        .join(' · ');
    const later = r.endLocal && r.startLocal && r.endLocal.slice(0, 10) !== r.startLocal.slice(0, 10);
    const from = [d.from, hm(r.startLocal), zoneLabel(r.startTz, instant(r.startLocal, r.startTz))]
      .filter(Boolean)
      .join(' ');
    const to = [
      d.to,
      r.endLocal ? `${hm(r.endLocal)}${later ? ' +1' : ''}` : '',
      zoneLabel(r.endTz ?? r.startTz, instant(r.endLocal, r.endTz ?? r.startTz)),
    ]
      .filter(Boolean)
      .join(' ');
    return [[from, to].filter(Boolean).join(' → '), dur(r)].filter(Boolean).join(' · ');
  }
  if (r.kind === 'lodging' && role === 'starts' && r.startLocal && r.endLocal) {
    const nights = Math.round(
      (Date.parse(r.endLocal.slice(0, 10)) - Date.parse(r.startLocal.slice(0, 10))) / 86_400_000,
    );
    return `Check in ${hm(r.startLocal)} · ${nights} night${nights === 1 ? '' : 's'} · out ${flag(r.endLocal.slice(0, 10))} ${hm(r.endLocal)}`;
  }
  return '';
}

/** Bookings that began on an earlier day: arriving or checking out today, or still staying. */
function BookingMarkers({
  model,
  date,
  part: role,
}: {
  model: TripModel;
  date: string;
  part: 'ends' | 'continues';
}) {
  const marks = model.reservations.filter((r) => {
    const start = r.startLocal?.slice(0, 10);
    const end = r.endLocal?.slice(0, 10) ?? start;
    if (!start || !end || start >= date) return false;
    return role === 'ends' ? end === date : r.kind === 'lodging' && date < end;
  });
  if (marks.length === 0) return null;
  return (
    <ol className={s.rows}>
      {marks.map((r) => {
        const title = findItem(model, r.itemId ?? '')?.title ?? 'Booking';
        const heading = r.kind === 'lodging' ? (role === 'ends' ? 'Check out' : 'Staying at') : 'Arrives';
        return (
          <li key={`${r.id}-${role}`} className={s.row}>
            <ItineraryRow
              {...defined({
                time: role === 'ends' ? time(r.endLocal) : null,
                subline: [heading, bookingLine(r, role)].filter(Boolean).join(' · '),
              })}
              icon={icon({ kind: 'reservation' } as Item, r)}
              title={title}
              variant="logged"
            />
          </li>
        );
      })}
    </ol>
  );
}

function icon(item: Item, booking?: Reservation) {
  if (booking)
    return booking.kind === 'flight'
      ? Plane
      : booking.kind === 'lodging'
        ? BedDouble
        : booking.kind === 'train'
          ? TrainFront
          : Ticket;
  if (item.kind === 'note') return NotebookText;
  const c = (item.place?.category ?? '').toLowerCase();
  if (/restaurant|food|cafe|bar|ramen|izakaya/.test(c)) return Utensils;
  if (/hotel|lodging|hostel/.test(c)) return BedDouble;
  if (/museum|gallery/.test(c)) return Landmark;
  if (/shop|store|mall|market/.test(c)) return ShoppingBag;
  if (/station|railway/.test(c)) return TrainFront;
  if (item.listId) return Flag;
  return PinIcon;
}

/** Straight-line distance between two places, and a walking estimate (5 km/h, 1.3× for streets). */
function legText(a: Item, b: Item): string | null {
  if (a.place?.lat == null || a.place.lng == null || b.place?.lat == null || b.place.lng == null) return null;
  const r = Math.PI / 180;
  const dLat = (b.place.lat - a.place.lat) * r,
    dLng = (b.place.lng - a.place.lng) * r;
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(a.place.lat * r) * Math.cos(b.place.lat * r) * Math.sin(dLng / 2) ** 2;
  const m = 2 * 6_371_000 * Math.asin(Math.min(1, Math.sqrt(h)));
  if (m < 3000)
    return `≈ ${Math.max(1, Math.round(((m * 1.3) / 5000) * 60))} min · ${((m * 1.3) / 1000).toFixed(1)} km`;
  return `≈ ${(m / 1000).toFixed(1)} km · transit`;
}

function DropList({
  items,
  model,
  selected,
  onSelect,
  onDrop,
  onDelete,
  targets,
  onMove,
  showLegs = false,
  nowId = null,
  visits = [],
  tz = null,
  date = null,
}: {
  items: Planned[];
  model: TripModel;
  /** The stop happening now (today only). */
  nowId?: string | null;
  /** My visits that day, shown on their stops. */
  visits?: TripModel['visits'];
  tz?: string | null;
  /** The day the rows are on, for opening-hours warnings (FR-MAP-10). */
  date?: string | null;
  selected: string | null;
  onSelect: (id: string) => void;
  onDrop: (id: string, before?: Planned) => void;
  onDelete: (p: Planned) => void;
  /** The keyboard path for drag and drop: "day:<id>" or "list:<id>". */
  targets: { value: string; label: string }[];
  onMove: (item: Item, target: string) => void;
  showLegs?: boolean;
}) {
  const [over, setOver] = useState<string | null>(null);
  const bookingOf = (id: string) => model.reservations.find((r) => r.itemId === id);
  const drop = (e: DragEvent, before?: Planned) => {
    e.preventDefault();
    setOver(null);
    const id = e.dataTransfer.getData('text/pf-item');
    if (id && id !== before?.item.id) onDrop(id, before);
  };
  return (
    <ol className={s.rows} onDragOver={(e) => e.preventDefault()} onDrop={(e) => drop(e)}>
      {items.map((p, i) => {
        const b = bookingOf(p.item.id);
        const next = items[i + 1];
        const leg = showLegs && next ? legText(p.item, next.item) : null;
        const tags = [
          b?.confirmation ? (
            <DataTag key="c" label="Conf" value={b.confirmation} spoken="Confirmation code" />
          ) : null,
          hourTag(p.item, date),
        ].filter(Boolean);
        return (
          <li
            key={p.item.id}
            className={cx(s.row, selected === p.item.id && s.selectedRow, over === p.item.id && s.dropBefore)}
            draggable
            onDragStart={(e) => e.dataTransfer.setData('text/pf-item', p.item.id)}
            onDragOver={(e) => {
              e.preventDefault();
              setOver(p.item.id);
            }}
            onDragLeave={() => setOver(null)}
            onDrop={(e) => {
              e.stopPropagation();
              drop(e, p);
            }}
          >
            <ItineraryRow
              {...defined({
                time: time(b?.startLocal ?? p.item.startLocal),
                subline: b
                  ? bookingLine(b, 'starts')
                  : (visitLine(
                      visits.find((v) => v.itemId === p.item.id),
                      tz,
                    ) ?? [p.item.place?.localName, p.item.place?.address].filter(Boolean).join(' · ')),
                stopId: p.stopId,
              })}
              icon={icon(p.item, b)}
              title={p.item.title || 'Untitled'}
              tags={tags}
              state={
                nowId === p.item.id ? 'now' : visits.some((v) => v.itemId === p.item.id) ? 'past' : 'normal'
              }
            />
            {p.subItems.map((sub) => (
              <ItineraryRow
                key={sub.item.id}
                {...defined({ stopId: sub.stopId })}
                icon={icon(sub.item)}
                title={sub.item.title}
                variant="sub"
              />
            ))}
            <div className={s.rowTools}>
              {p.item.place?.lat != null && (
                <button
                  type="button"
                  className={s.tool}
                  aria-label={`Show ${p.item.title} on the map`}
                  onClick={() => onSelect(p.item.id)}
                >
                  <Crosshair size={14} />
                </button>
              )}
              <select
                className={cx(s.moveTo, t.caption)}
                aria-label={`Move ${p.item.title} to`}
                value=""
                onChange={(e) => onMove(p.item, e.target.value)}
              >
                <option value="">Move…</option>
                {targets.map((x) => (
                  <option key={x.value} value={x.value}>
                    {x.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className={s.tool}
                aria-label={`Remove ${p.item.title}`}
                onClick={() => onDelete(p)}
              >
                <Trash2 size={14} />
              </button>
            </div>
            {leg && <Leg icon={Footprints} text={leg} />}
          </li>
        );
      })}
    </ol>
  );
}

/** The stop's hours against its visit: a hazard tag when closed, not open yet or shut before it ends;
 * else the hours as they are. */
function hourTag(item: Item, date: string | null) {
  const hours = item.place?.openingHours;
  if (!hours) return null;
  const w = hoursWarning(hours, date, time(item.startLocal), item.durationMin);
  if (!w) return <DataTag key="h" label="Open" value={hours.slice(0, 16)} readout />;
  if (w.kind === 'closed')
    return (
      <DataTag
        key="h"
        label="Closed"
        value={DAY_NAMES[w.weekday] ?? ''}
        tone="hazard"
        readout={false}
        spoken={`Closed on ${DAY_NAMES[w.weekday]}`}
      />
    );
  return (
    <DataTag
      key="h"
      label={w.kind === 'opens' ? 'Opens' : 'Closes'}
      value={clock(w.at)}
      tone="hazard"
      spoken={
        w.kind === 'opens'
          ? `Not open yet: opens at ${clock(w.at)}`
          : `Closes at ${clock(w.at)}, before the visit ends`
      }
    />
  );
}

/** "Visited 07:41–09:20" (FR-TRK-05). */
function visitLine(v: TripModel['visits'][number] | undefined, tz: string | null): string | null {
  if (!v?.arriveAt) return null;
  const at = (iso: string) => wallTime(Date.parse(iso), tz);
  return `Visited ${at(v.arriveAt)}–${v.departAt ? at(v.departAt) : 'now'}`;
}

function DayChips({
  model,
  current,
  isToday,
  onPick,
  onDrop,
}: {
  model: TripModel;
  current?: string | undefined;
  isToday: (d: Day) => boolean;
  onPick: (id: string) => void;
  onDrop: (itemId: string, dayId: string) => void;
}) {
  return (
    <div className={s.chips} role="tablist" aria-label="Days">
      {model.days.map((d) => (
        <button
          key={d.id}
          type="button"
          role="tab"
          aria-selected={d.id === current}
          aria-label={`Day ${d.number}, ${d.date}`}
          className={cx(s.chip, t.readoutS, d.id === current && s.chipOn, isToday(d) && s.chipToday)}
          onClick={() => onPick(d.id)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            const id = e.dataTransfer.getData('text/pf-item');
            if (id) onDrop(id, d.id);
          }}
        >
          {pad(d.number)}
        </button>
      ))}
    </div>
  );
}

/** Search places near the day (Geoapify/Photon via the Worker) and add one at the end of the day. */
function AddPlace({ day, tripId, run }: { day: Day; tripId: string; run: (edits: Edit[]) => Promise<void> }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<PlaceResult[]>([]);
  const near = day.items.map((p) => p.item.place).find((p) => p?.lat != null);
  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      const params = new URLSearchParams({
        q: q.trim(),
        ...(near?.lat != null && near.lng != null ? { lat: String(near.lat), lng: String(near.lng) } : {}),
      });
      try {
        setResults((await api<{ results: PlaceResult[] }>(`/places/search?${params}`)).results);
      } catch {
        setResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [q, near?.lat, near?.lng]);
  const add = async (r: PlaceResult | null) => {
    const placeId = newUuidv7();
    const itemId = newUuidv7();
    const sortKey = keyBetween(day.items.at(-1)?.item.sortKey ?? null, null);
    const edits: Edit[] = [];
    if (r)
      edits.push({
        table: 'place',
        id: placeId,
        tripId,
        fields: {
          name: r.name,
          lat: r.lat,
          lng: r.lng,
          address: r.address ?? null,
          category: r.category ?? null,
          osmId: r.osm ?? null,
          source: r.source,
        },
      });
    edits.push({
      table: 'item',
      id: itemId,
      tripId,
      fields: {
        title: r?.name ?? q.trim(),
        kind: 'place',
        dayId: day.id,
        listId: null,
        sortKey,
        tz: day.tz,
        placeId: r ? placeId : null,
      },
    });
    setQ('');
    setResults([]);
    await run(edits);
  };
  return (
    <div className={s.search}>
      <label className={s.searchBox}>
        <Search size={18} aria-hidden="true" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search places"
          aria-label="Search places to add to this day"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && q.trim()) void add(results[0] ?? null);
          }}
        />
        {q && (
          <button type="button" aria-label="Clear" onClick={() => setQ('')}>
            <X size={16} />
          </button>
        )}
      </label>
      {(results.length > 0 || q.trim().length >= 2) && (
        <ul className={s.results}>
          {results.map((r) => (
            <li key={`${r.name}${r.lat}`}>
              <button type="button" onClick={() => void add(r)}>
                <span className={t.titleM}>{r.name}</span>
                {r.address && <span className={cx(t.caption, s.muted)}>{r.address}</span>}
              </button>
            </li>
          ))}
          <li>
            <button type="button" onClick={() => void add(null)}>
              <span className={t.bodyS}>Add “{q.trim()}” as written</span>
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}

function DayMap({
  model,
  day,
  selected,
  onSelect,
  me,
  partnerName,
  nowId,
}: {
  model: TripModel;
  day: Day;
  selected: string | null;
  onSelect: (id: string) => void;
  me?: string | undefined;
  partnerName?: string | undefined;
  /** The stop on now, shown in the place card until another is chosen. */
  nowId: string | null;
}) {
  const catalog = useMapCatalog();
  const hue = `var(--color-day-${((day.number - 1) % 8) + 1})`;
  const { pins, routes, points } = useMemo(() => {
    const pins: MapPin[] = [];
    const line: [number, number][] = [];
    for (const p of day.items) {
      for (const x of [p, ...p.subItems]) {
        const pl = x.item.place;
        if (pl?.lat == null || pl.lng == null) continue;
        pins.push({
          id: x.item.id,
          lat: pl.lat,
          lng: pl.lng,
          label: x.stopId?.split('-').pop() ?? '·',
          kind: 'day',
          color: hue,
          title: `Stop ${x.stopId}, ${x.item.title}`,
        });
        if (x === p) line.push([pl.lng, pl.lat]);
      }
    }
    for (const r of model.reservations.filter(
      (r) =>
        r.kind === 'lodging' &&
        (r.startLocal ?? '') <= `${day.date}T99` &&
        day.date < (r.endLocal ?? '').slice(0, 10),
    )) {
      const it = findItem(model, r.itemId ?? '');
      if (it?.place?.lat != null && it.place.lng != null)
        pins.push({
          id: it.id,
          lat: it.place.lat,
          lng: it.place.lng,
          label: '',
          kind: 'lodging',
          title: `Hotel, ${it.title}`,
        });
    }
    const routes: MapRoute[] = line.length > 1 ? [{ coords: line, color: hue }] : [];
    return { pins, routes, points: pins.map((p) => [p.lat, p.lng] as [number, number]) };
  }, [day, model, hue]);
  const region = regionFor(catalog.data, points);
  const boundsKey = useMemo(() => {
    if (points.length === 0) return 'null';
    const lats = points.map((p) => p[0]),
      lngs = points.map((p) => p[1]);
    const padLat = Math.max((Math.max(...lats) - Math.min(...lats)) * 0.15, 0.007),
      padLng = Math.max((Math.max(...lngs) - Math.min(...lngs)) * 0.15, 0.009);
    return JSON.stringify([
      [Math.min(...lngs) - padLng, Math.min(...lats) - padLat],
      [Math.max(...lngs) + padLng, Math.max(...lats) + padLat],
    ]);
  }, [points]);
  // By value, so a refetch with the same places doesn't rebuild the map.
  const bounds = useMemo(
    () => JSON.parse(boundsKey) as [[number, number], [number, number]] | null,
    [boundsKey],
  );
  const cardId = selected ?? nowId;
  const chosen = cardId ? findItem(model, cardId) : null;
  const mine = walked(model, day, me);
  const theirs = model.segments
    .filter((x) => x.dateLocal === day.date && x.userId !== me)
    .reduce((a, x) => a + x.distanceM, 0);
  const visit = chosen ? model.visits.find((v) => v.itemId === chosen.id && v.userId === me) : undefined;
  if (!bounds)
    return <p className={cx(s.pad, t.bodyS, s.muted)}>No places with a location on this day yet.</p>;
  if (!region)
    return (
      <p className={cx(s.pad, t.bodyS, s.muted)}>
        {catalog.isPending ? 'Loading the map list…' : 'No offline map covers these places yet.'}
      </p>
    );
  return (
    <div className={s.mapWrap}>
      <FieldMap
        tiles={
          // The dev simulator's Kyoto sample lives in public/ (make map-sample).
          region.name.startsWith('sample-')
            ? `/map/samples/${region.name}.pmtiles`
            : `/v1/maps/${region.name}.pmtiles`
        }
        bounds={bounds}
        label={`Map of day ${day.number}`}
        pins={pins}
        routes={routes}
        selected={selected}
        onSelect={onSelect}
      />
      {/* W-01: what the lines are, top left; the stop on now (or chosen) at the foot. */}
      <div className={s.legend}>
        <MapLegend
          lines={[
            { label: 'Planned', color: hue },
            ...(mine > 0 ? [{ label: 'Me', detail: km(mine), color: 'var(--color-text-primary)' }] : []),
            ...(theirs > 0
              ? [
                  {
                    label: partnerName ?? 'Partner',
                    detail: km(theirs),
                    color: 'var(--color-text-primary)',
                    dashed: true,
                  },
                ]
              : []),
          ]}
        />
      </div>
      {chosen && (
        <div className={s.card}>
          <MapPlaceCard
            stopId={findStop(day, chosen.id)}
            state={chosen.id === nowId ? 'Now' : flag(day.date)}
            meta={visit?.arriveAt ? `Arrived ${wallTime(Date.parse(visit.arriveAt), day.tz)}` : undefined}
            title={chosen.title}
            detail={[
              chosen.place?.localName,
              chosen.place?.category?.replaceAll('_', ' '),
              chosen.place?.address,
            ]
              .filter(Boolean)
              .join(' · ')}
            photo={chosen.place?.photoUrl ?? null}
            tags={
              <>
                {time(chosen.startLocal) && <DataTag label="Plan" value={time(chosen.startLocal) ?? ''} />}
                {chosen.place?.openingHours && <DataTag label="Open" value={chosen.place.openingHours} />}
              </>
            }
          />
        </div>
      )}
    </div>
  );
}

function findStop(day: Day, id: string) {
  for (const p of day.items) for (const x of [p, ...p.subItems]) if (x.item.id === id) return x.stopId ?? '';
  return '';
}
